import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mic, Square } from 'lucide-react';
import { currentLocale } from '../i18n.js';
import { cn } from '../lib/cn.js';
import { useSession } from '../lib/session.js';
import { VoiceJobDialog } from './VoiceJobDialog.js';

/**
 * QUO-023 — speak the service description instead of typing it.
 *
 * The browser's own speech recognition, not a service of ours: nothing is
 * uploaded by Worki, and there is no key to hold. What that costs is coverage —
 * Chrome and Edge have it, Firefox does not — so the button is only rendered
 * where the API exists, rather than appearing everywhere and failing on press.
 *
 * With the voice service on (`features.voice`) the same button goes through
 * our own Hebrew speech-to-text instead — the live voice screen in its dictate
 * mode — and the text is appended to the field when it is ready. Where the
 * service is off (Starter plan, or not configured) the browser's recognition
 * stays as it was, below.
 *
 * Recognition returns the sentence so far on every event, replacing what it
 * said before, so the text typed before dictation started is pinned and each
 * result is appended to that instead of to the running value. Otherwise a
 * correction mid-sentence duplicates everything said up to it.
 */

interface RecognitionEvent {
  resultIndex: number;
  results: { isFinal: boolean; 0: { transcript: string }; length: number }[] & { length: number };
}

interface Recognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  onresult: ((event: RecognitionEvent) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
}

type RecognitionCtor = new () => Recognition;

function recognitionCtor(): RecognitionCtor | null {
  const win = window as unknown as {
    SpeechRecognition?: RecognitionCtor;
    webkitSpeechRecognition?: RecognitionCtor;
  };
  return win.SpeechRecognition ?? win.webkitSpeechRecognition ?? null;
}

interface DictateProps {
  value: string;
  onChange: (text: string) => void;
  className?: string;
}

/** The field's text with the dictated words after it, a space between when there is text. */
export function appendDictation(field: string, spoken: string): string {
  const words = spoken.trim();
  const text = field.trim();
  if (!words) return field;
  return text ? `${text} ${words}` : words;
}

const buttonClass = 'h-9 w-9 shrink-0 rounded-lg flex items-center justify-center transition-colors';

export function DictateButton(props: DictateProps) {
  const { session } = useSession();
  return session?.features.voice ? <ServerDictateButton {...props} /> : <BrowserDictateButton {...props} />;
}

function ServerDictateButton({ value, onChange, className }: DictateProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('quotes.dictate')}
        title={t('quotes.dictate')}
        className={cn(buttonClass, 'bg-muted text-muted-foreground hover:bg-accent', className)}
      >
        <Mic className="h-4 w-4" />
      </button>
      {open && (
        <VoiceJobDialog
          dictate
          onReady={(job) => {
            setOpen(false);
            if (job.transcript) onChange(appendDictation(value, job.transcript));
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function BrowserDictateButton({ value, onChange, className }: DictateProps) {
  const { t } = useTranslation();
  const [listening, setListening] = useState(false);
  const recognition = useRef<Recognition | null>(null);
  /** The text as it stood when the microphone opened. */
  const base = useRef('');
  /** Read inside the recognition callbacks, which are registered once. */
  const emit = useRef(onChange);
  emit.current = onChange;

  const supported = typeof window !== 'undefined' && recognitionCtor() !== null;

  // A microphone left open after the screen closes keeps the browser's
  // recording indicator on, which reads as the app listening in the background.
  useEffect(() => () => recognition.current?.stop(), []);

  if (!supported) return null;

  const stop = () => {
    recognition.current?.stop();
    recognition.current = null;
    setListening(false);
  };

  const start = () => {
    const Ctor = recognitionCtor();
    if (!Ctor) return;

    const engine = new Ctor();
    engine.lang = currentLocale() === 'he' ? 'he-IL' : 'en-US';
    engine.continuous = true;
    engine.interimResults = true;

    base.current = value.trim();

    engine.onresult = (event) => {
      let spoken = '';
      for (let index = 0; index < event.results.length; index += 1) {
        spoken += event.results[index]![0].transcript;
      }
      const prefix = base.current ? `${base.current} ` : '';
      emit.current(`${prefix}${spoken.trim()}`);
    };
    // Both a refusal (no permission, no network) and a natural pause end the
    // session; either way the button has to stop claiming to be listening.
    engine.onerror = () => stop();
    engine.onend = () => setListening(false);

    recognition.current = engine;
    setListening(true);
    engine.start();
  };

  return (
    <button
      type="button"
      onClick={() => (listening ? stop() : start())}
      aria-label={t(listening ? 'quotes.dictateStop' : 'quotes.dictate')}
      title={t(listening ? 'quotes.dictateStop' : 'quotes.dictate')}
      aria-pressed={listening}
      className={cn(
        buttonClass,
        listening
          ? 'bg-destructive/10 text-destructive animate-pulse'
          : 'bg-muted text-muted-foreground hover:bg-accent',
        className,
      )}
    >
      {listening ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
    </button>
  );
}
